/** One working-hours slot exactly as staff.setWorkingHours accepts it. */
export type HourSlot = { dayOfWeek: number; startTime: string; endTime: string };

/** One day's row in the wizard's per-day hours editor - `dow` is 0-6 (0 =
 *  Sunday). `start`/`end` are "HH:MM", meaningful only while `isOpen`. */
export type DayHours = { dow: number; isOpen: boolean; start: string; end: string };

// Sensible starting point for a newly-opened day - editable right after,
// same defaults `app/staff-schedule.tsx`'s real per-day editor seeds with,
// so a business set up here and later adjusted in settings doesn't jump.
export const DEFAULT_HOURS_START = '09:00';
export const DEFAULT_HOURS_END = '18:00';
/** Friday closes early - most Israeli businesses stop mid-afternoon before
 *  Shabbat, unlike every other weekday's full 09:00-18:00. */
export const FRIDAY_HOURS_END = '14:00';

/** The window a day gets the first time it is switched open. */
export function defaultHoursFor(dow: number): { start: string; end: string } {
  return {
    start: DEFAULT_HOURS_START,
    end: dow === 5 ? FRIDAY_HOURS_END : DEFAULT_HOURS_END,
  };
}

/** Sunday through Thursday - the days a new week starts out OPEN on. */
export const DEFAULT_OPEN_DAYS: readonly number[] = [0, 1, 2, 3, 4];

/**
 * The week a staff member starts with: Sunday to Thursday, 09:00-18:00, with
 * Friday and Saturday closed (owner, 2026-08-12).
 *
 * It used to be seven closed rows, which made the commonest answer in Israel
 * the most work: five switches and ten time fields per person before anything
 * could be booked. A prefilled week turns the chapter into something to
 * CONFIRM instead of something to build.
 *
 * The consequence is deliberate and worth naming: a staff member now satisfies
 * the server's "has at least one open day" readiness rule the moment they are
 * added. `chaptersWithAnyAnswer` therefore no longer treats hours as evidence
 * of a visit - see its comment in `chapters.ts` - or the hours card would turn
 * green off a default the owner never looked at.
 *
 * Closed days keep EMPTY times rather than hidden ones, so switching Friday on
 * still runs the same "seed a blank day" path everything else uses, and its
 * short pre-Shabbat default applies.
 */
export function defaultDayHours(): DayHours[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dow) => {
    const isOpen = DEFAULT_OPEN_DAYS.includes(dow);
    return {
      dow,
      isOpen,
      start: isOpen ? defaultHoursFor(dow).start : '',
      end: isOpen ? defaultHoursFor(dow).end : '',
    };
  });
}

/** Every OPEN day's own start/end, in day order - the per-day editor's rows
 *  flattened into the shape `staff.setWorkingHours` takes. Closed days are
 *  simply absent, same "isOpen -> included" rule `staff-schedule.tsx` uses. */
export function toHourSlots(rows: DayHours[]): HourSlot[] {
  return rows
    .filter((r) => r.isOpen)
    .map((r) => ({ dayOfWeek: r.dow, startTime: r.start, endTime: r.end }));
}
